/**
 * AccountActivation Component
 *
 * Account activation form with invitation token validation and password creation.
 * Designed for seamless user activation flow with mobile-responsive design.
 */

// 1. External dependencies
import React, { useState, useCallback, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { Eye, EyeOff, CheckCircle, XCircle, Loader2 } from 'lucide-react';

// 2. Internal absolute imports
import { Button, Input, Form, Alert, Link, Card, CardBody, CardFooter } from '@enfomentis/ui';
import { AppLayout } from '@/components/shared/layouts';
import { Brand } from '@enfomentis/shared/config';
import { useAuth } from '@/hooks/useAuth';
import { invitationTokenService } from '@/services/InvitationTokenService';
import { betaApplicationsRepository } from '@/repositories/betaApplicationsRepository';
import type { InvitationTokenValidation } from '@/types/invitation';

/**
 * Account Activation Component
 *
 * Features:
 * - Token validation on component mount
 * - Password creation form with validation and show/hide functionality
 * - Confirm password matching validation
 * - Error states for expired/invalid tokens
 * - Success state with automatic login redirect
 * - Mobile-responsive design following LoginForm/AdminAuth patterns
 */
export function AccountActivation(): React.JSX.Element {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { signup } = useAuth();

  // Token validation state
  const [tokenValidation, setTokenValidation] = useState<InvitationTokenValidation | null>(null);
  const [isValidatingToken, setIsValidatingToken] = useState(true);

  // Form state
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] = useState(false);

  // Account activation success state
  const [isActivationComplete, setIsActivationComplete] = useState(false);

  // Toggle password visibility
  const togglePasswordVisibility = useCallback(() => {
    setIsPasswordVisible(prev => !prev);
  }, []);

  const toggleConfirmPasswordVisibility = useCallback(() => {
    setIsConfirmPasswordVisible(prev => !prev);
  }, []);

  // Password validation function
  const validatePassword = (value: string): string | null => {
    if (!value) return "Password is required";
    if (value.length < 8) return "Password must be at least 8 characters long";
    if (!/(?=.*[a-z])/.test(value)) return "Password must contain at least one lowercase letter";
    if (!/(?=.*[A-Z])/.test(value)) return "Password must contain at least one uppercase letter";
    if (!/(?=.*\d)/.test(value)) return "Password must contain at least one number";
    return null;
  };

  // Confirm password validation function
  const validateConfirmPassword = (value: string): string | null => {
    if (!value) return "Please confirm your password";
    if (value !== formData.password) return "Passwords do not match";
    return null;
  };

  // Validation state - show errors after submit
  const passwordError = hasSubmitted ? validatePassword(formData.password) : null;
  const confirmPasswordError = hasSubmitted ? validateConfirmPassword(formData.confirmPassword) : null;

  // Validate token on component mount
  useEffect(() => {
    const validateToken = async (): Promise<void> => {
      if (!token) {
        setTokenValidation({
          isValid: false,
          error: 'not_found'
        });
        setIsValidatingToken(false);
        return;
      }

      try {
        const validation = await invitationTokenService.validateToken(token);
        setTokenValidation(validation);
      } catch (error) {
        console.error('Token validation error:', error);
        setTokenValidation({
          isValid: false,
          error: 'not_found'
        });
      } finally {
        setIsValidatingToken(false);
      }
    };

    validateToken();
  }, [token]);

  // Form submission handler
  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setHasSubmitted(true);
    setIsSubmitting(true);
    setServerErrors({});

    try {
      // Validate form fields
      const passwordValidationError = validatePassword(formData.password);
      const confirmPasswordValidationError = validateConfirmPassword(formData.confirmPassword);

      if (passwordValidationError || confirmPasswordValidationError) {
        setIsSubmitting(false);
        return;
      }

      if (!token || !tokenValidation?.isValid || !tokenValidation.token?.betaApplications) {
        setServerErrors({ 'general': 'Invalid or expired invitation token' });
        setIsSubmitting(false);
        return;
      }

      // Get beta application data (user doesn't exist yet!)
      const betaApplication = tokenValidation.token.betaApplications;

      if (!betaApplication) {
        setServerErrors({ 'general': 'Invalid invitation token - missing application data' });
        setIsSubmitting(false);
        return;
      }

      // Create Supabase Auth user via AuthProvider's signup primitive
      const signupResult = await signup({
        email: betaApplication.email,
        password: formData.password,
        options: {
          emailRedirectTo: `${window.location.origin}/onboarding`,
          data: {
            full_name: betaApplication.fullName
          }
        }
      });

      if (!signupResult.success) {
        console.error('Auth signup error:', signupResult.error, signupResult.message);
        setServerErrors({ 'general': signupResult.message });
        setIsSubmitting(false);
        return;
      }

      console.log('✅ AccountActivation: Auth user created:', signupResult.userId);
      console.log('📝 AccountActivation: public.users will be auto-created by database trigger');

      // NEW FLOW: public.users is automatically created by trigger when auth.users is created!
      // No need to update public.users - the trigger handles:
      // - Creating public.users with same ID as auth.users.id
      // - Setting status='active'
      // - Setting activated_at=NOW()
      // - Copying full_name from auth.users.raw_user_meta_data

      // Mark invitation token as used and link to new user
      if (tokenValidation.token) {
        await invitationTokenService.markAsUsed(tokenValidation.token.id, signupResult.userId);
        console.log('✅ AccountActivation: Invitation token marked as used and linked to user');
      }

      // Update beta_application status to 'activated' and link to user
      if (betaApplication) {
        try {
          // Fetch full application record to get updatedAt for optimistic concurrency
          const fullApplication = await betaApplicationsRepository.getById(betaApplication.id);
          if (fullApplication) {
            await betaApplicationsRepository.update(
              betaApplication.id,
              {
                status: 'activated',
                userId: signupResult.userId
              },
              fullApplication.updatedAt
            );
            console.log('✅ AccountActivation: Beta application marked as activated and linked to user');
          }
        } catch (updateError) {
          console.error('❌ AccountActivation: Failed to update beta_application status:', updateError);
          // Don't fail the activation - just log the error
        }
      }

      // Show success state
      setIsActivationComplete(true);

      // User is already logged in (Supabase auto-login after signUp)
      // Redirect to onboarding after showing success message
      setTimeout(() => {
        console.log('✅ AccountActivation: Redirecting to onboarding (user already authenticated)');
        navigate('/onboarding', { replace: true });
      }, 2000); // 2 second delay to show success state

    } catch (error) {
      console.error('Account activation error:', error);
      setServerErrors({ 'general': 'Account activation failed. Please try again.' });
      setIsSubmitting(false);
    }
  };

  // Render loading state during token validation
  if (isValidatingToken) {
    return (
      <AppLayout hideFooter={true}>
        <Card className="items-center justify-center h-full" radius="none" shadow="none">
          <CardBody className="flex max-w-md mx-auto p-6 mt-10 text-center">
            <div className="space-y-4">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
              <p className="text-lg">Validating invitation...</p>
            </div>
          </CardBody>
        </Card>
      </AppLayout>
    );
  }

  // Render invalid token state
  if (!tokenValidation?.isValid) {
    const getErrorMessage = (): string => {
      switch (tokenValidation?.error) {
        case 'expired':
          return 'This invitation has expired. Please contact an administrator for a new invitation.';
        case 'already_used':
          return 'This invitation has already been used. If you need help accessing your account, please contact support.';
        case 'not_found':
        default:
          return 'This invitation link is invalid or does not exist. Please check the link and try again.';
      }
    };

    return (
      <AppLayout hideFooter={true}>
        <Card className="items-center justify-center h-full" radius="none" shadow="none">
          <CardBody className="flex max-w-md mx-auto p-6 mt-10 text-center">
            <div className="space-y-4">
              <XCircle className="w-16 h-16 mx-auto text-danger" />
              <h1 className="text-xl font-semibold">Invalid Invitation</h1>
              <p className="text-default-600">{getErrorMessage()}</p>
            </div>
          </CardBody>
          <CardFooter className="max-w-md mx-auto justify-center border-t border-default-100">
            <div className="text-center">
              <p className="text-sm text-text-secondary">
                Need help?{' '}
                <RouterLink to="/beta-application">
                  <Link color="primary" size="sm">
                    Apply for beta access
                  </Link>
                </RouterLink>
              </p>
            </div>
          </CardFooter>
        </Card>
      </AppLayout>
    );
  }

  // Render success state
  if (isActivationComplete) {
    return (
      <AppLayout hideFooter={true}>
        <Card className="items-center justify-center h-full" radius="none" shadow="none">
          <CardBody className="flex max-w-md mx-auto p-6 mt-10 text-center">
            <div className="space-y-4">
              <CheckCircle className="w-16 h-16 mx-auto text-success" />
              <h1 className="text-xl font-semibold">Account Activated!</h1>
              <p className="text-default-600">
                Welcome to {Brand.displayName}, {tokenValidation.token?.betaApplications?.fullName}!
              </p>
              <p className="text-sm text-default-500">
                You'll be automatically signed in and redirected to your dashboard in a moment...
              </p>
              <div className="flex items-center justify-center space-x-2 text-primary">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">Signing you in...</span>
              </div>
            </div>
          </CardBody>
        </Card>
      </AppLayout>
    );
  }

  // Render account activation form
  return (
    <AppLayout hideFooter={true}>
      <Card className="items-center justify-center h-full" radius="none" shadow="none">
        <CardBody className="flex max-w-md mx-auto p-6 mt-10">
          <div className="space-y-6">
            {/* Welcome Message */}
            <div className="text-center space-y-2">
              <h1 className="text-xl font-semibold">Activate Your Account</h1>
              <p className="text-default-600">
                Welcome, {tokenValidation.token?.betaApplications?.fullName}! Create your password to complete account activation.
              </p>
            </div>

            <Form
              validationBehavior="aria"
              validationErrors={serverErrors}
              onSubmit={handleSubmit}
              className="space-y-6"
            >
              {/* Password Input */}
              <Input
                type={isPasswordVisible ? "text" : "password"}
                label="Password"
                placeholder="Create your password"
                value={formData.password}
                onValueChange={(value) => setFormData(prev => ({ ...prev, password: value }))}
                validate={validatePassword}
                isRequired
                autoComplete="new-password"
                errorMessage={serverErrors['password'] || passwordError}
                isInvalid={!!serverErrors['password'] || !!passwordError}
                endContent={
                  <button
                    className="focus:outline-none"
                    type="button"
                    onClick={togglePasswordVisibility}
                    aria-label={isPasswordVisible ? "Hide password" : "Show password"}
                  >
                    {isPasswordVisible ? (
                      <Eye className="w-4 h-4 text-default-400 pointer-events-none" />
                    ) : (
                      <EyeOff className="w-4 h-4 text-default-400 pointer-events-none" />
                    )}
                  </button>
                }
                classNames={{
                  input: "text-base", // 16px font size to prevent mobile zoom
                }}
              />

              {/* Confirm Password Input */}
              <Input
                type={isConfirmPasswordVisible ? "text" : "password"}
                label="Confirm Password"
                placeholder="Confirm your password"
                value={formData.confirmPassword}
                onValueChange={(value) => setFormData(prev => ({ ...prev, confirmPassword: value }))}
                validate={validateConfirmPassword}
                isRequired
                autoComplete="new-password"
                errorMessage={serverErrors['confirmPassword'] || confirmPasswordError}
                isInvalid={!!serverErrors['confirmPassword'] || !!confirmPasswordError}
                endContent={
                  <button
                    className="focus:outline-none"
                    type="button"
                    onClick={toggleConfirmPasswordVisibility}
                    aria-label={isConfirmPasswordVisible ? "Hide password" : "Show password"}
                  >
                    {isConfirmPasswordVisible ? (
                      <Eye className="w-4 h-4 text-default-400 pointer-events-none" />
                    ) : (
                      <EyeOff className="w-4 h-4 text-default-400 pointer-events-none" />
                    )}
                  </button>
                }
                classNames={{
                  input: "text-base", // 16px font size to prevent mobile zoom
                }}
              />

              {/* Password Requirements */}
              <div className="text-xs text-default-500 space-y-1">
                <p>Password requirements:</p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>At least 8 characters long</li>
                  <li>Contains uppercase and lowercase letters</li>
                  <li>Contains at least one number</li>
                </ul>
              </div>

              {/* General Error Display */}
              {serverErrors['general'] && (
                <Alert
                  color="warning"
                  variant="flat"
                  description={serverErrors['general']}
                />
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                color="primary"
                size="lg"
                isLoading={isSubmitting}
                className="w-full"
              >
                {isSubmitting ? 'Activating Account...' : 'Activate Account'}
              </Button>
            </Form>
          </div>
        </CardBody>

        {/* Help Text */}
        <CardFooter className="max-w-md mx-auto justify-center border-t border-default-100">
          <div className="text-center">
            <p className="text-sm text-text-secondary">
              Having trouble?{' '}
              <RouterLink to="/beta-application">
                <Link color="primary" size="sm">
                  Contact support
                </Link>
              </RouterLink>
            </p>
          </div>
        </CardFooter>
      </Card>
    </AppLayout>
  );
}